import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

export type CountryCostLineItem = {
  title: string;
  description: string;
  icon?: string;
};

export type CountryBlockedAccountModel = {
  badge?: string;
  title?: string;
  amount?: string;
  amountSuffix?: string;
  description?: string;
  bullets?: string[];
};

@Component({
  selector: 'app-marketing-country-costs',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="country-costs" id="costs">
      <div class="country-container country-costs__inner">
        <div class="country-costs__copy">
          @if (title) {
            <h2>{{ title }}</h2>
          }
          @if (intro) {
            <p>{{ intro }}</p>
          }
          @if (items.length) {
            <ul class="country-costs__list">
              @for (item of items; track item.title) {
                <li>
                  <div class="country-costs__icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.75"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      focusable="false"
                    >
                      @switch (iconKey(item.icon)) {
                        @case ('wallet') {
                          <rect width="20" height="14" x="2" y="5" rx="2" />
                          <path d="M2 10h20" />
                        }
                        @case ('home') {
                          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                          <polyline points="9 22 9 12 15 12 15 22" />
                        }
                        @case ('graduation') {
                          <path
                            d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"
                          />
                          <path d="M22 10v6" />
                          <path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5" />
                        }
                        @default {
                          <path
                            d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                          />
                          <path d="m9 12 2 2 4-4" />
                        }
                      }
                    </svg>
                  </div>
                  <div>
                    <strong>{{ item.title }}</strong>
                    <p>{{ item.description }}</p>
                  </div>
                </li>
              }
            </ul>
          }
        </div>

        @if (blocked; as card) {
          @if (card.title || card.amount) {
          <article class="country-blocked-card">
            <div class="country-blocked-card__head">
              @if (card.title) {
                <h3>{{ card.title }}</h3>
              }
              @if (card.badge) {
                <span class="country-blocked-card__badge">{{ card.badge }}</span>
              }
            </div>
            @if (card.description) {
              <p class="country-blocked-card__lead">{{ card.description }}</p>
            }
            @if (card.amount) {
              <p class="country-blocked-card__amount">
                {{ card.amount }}
                @if (card.amountSuffix) {
                  <span>{{ card.amountSuffix }}</span>
                }
              </p>
              <div class="country-blocked-card__meter" aria-hidden="true"><span></span></div>
            }
            @if (card.bullets?.length) {
              <ul class="country-blocked-card__bullets">
                @for (line of card.bullets!; track line) {
                  <li><span aria-hidden="true">✓</span> {{ line }}</li>
                }
              </ul>
            } @else if (card.note) {
              <p class="country-blocked-card__note">{{ card.note }}</p>
            }
          </article>
          }
        }
      </div>
    </section>
  `,
})
export class MarketingCountryCostsComponent {
  @Input() title = 'Understanding the Costs';
  @Input() intro?: string;
  @Input() items: CountryCostLineItem[] = [];
  @Input() blocked?: CountryBlockedAccountModel & { note?: string };

  iconKey(icon: string | undefined): string {
    return (icon ?? '').trim().toLowerCase();
  }
}
